import { useState } from "react";
import axios from "axios";
import { ArrowUpRight, CheckCircle2, AlertCircle } from "lucide-react";
import { useToast } from "../../hooks/use-toast";
import { contactInfo } from "../../mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const initial = { name: "", email: "", phone: "", message: "" };

const ContactSection = () => {
  const { toast } = useToast();
  const [form, setForm] = useState(initial);
  const [sending, setSending] = useState(false);
  const [errors, setErrors] = useState({});

  const onChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: undefined });
    }
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Please share your name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) next.email = "Enter a valid email.";
    if (form.message.trim().length < 5) next.message = "Tell us a little more about your project.";
    return next;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const nextErrors = validate();
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/inquiries`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        message: form.message.trim(),
      });
      setForm(initial);
      toast({
        title: data.delivered ? "Inquiry received." : "Inquiry saved.",
        description: data.delivered
          ? "Thank you — the ODDA studio will be in touch shortly."
          : "We’ve saved your message and will follow up shortly.",
        action: <CheckCircle2 size={18} />,
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast({
        variant: "destructive",
        title: "Something went wrong.",
        description:
          typeof detail === "string"
            ? detail
            : "Please try again in a moment, or email hello@oddastudio.com.",
        action: <AlertCircle size={18} />,
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="contact" id="contact">
      <div className="wrap contact-grid reveal">
        <div className="contact-left">
          <div className="kicker">08 — Start a conversation</div>
          <h2>
            Let’s make
            <br />
            something
            <br />
            <span className="accent">EMERGE.</span>
          </h2>
          <p className="contact-copy">
            Tell us about your project, the space you are imagining, and where you are in the process. We will take it from there.
          </p>
          <div className="contact-meta">
            <div>
              <span className="kicker">Direct</span>
              <a href={`mailto:${contactInfo.email}`} data-cursor="link">{contactInfo.email}</a>
            </div>
            <div>
              <span className="kicker">Studio</span>
              <p>{contactInfo.location}</p>
            </div>
          </div>
        </div>
        <form className="contact-form" onSubmit={onSubmit} noValidate>
          <label className={`field-group ${errors.name ? "field-group--error" : ""}`}>
            <span>Name{errors.name && <em> — {errors.name}</em>}</span>
            <input className="field" name="name" value={form.name} onChange={onChange} autoComplete="name" />
          </label>
          <label className={`field-group ${errors.email ? "field-group--error" : ""}`}>
            <span>Email{errors.email && <em> — {errors.email}</em>}</span>
            <input className="field" name="email" type="email" value={form.email} onChange={onChange} autoComplete="email" />
          </label>
          <label className="field-group">
            <span>Phone (optional)</span>
            <input className="field" name="phone" value={form.phone} onChange={onChange} autoComplete="tel" />
          </label>
          <label className={`field-group ${errors.message ? "field-group--error" : ""}`}>
            <span>Tell us about your project{errors.message && <em> — {errors.message}</em>}</span>
            <textarea className="field" name="message" value={form.message} onChange={onChange} rows={5} />
          </label>
          <button className="submit" type="submit" disabled={sending} data-cursor="link">
            <span>{sending ? "Sending…" : "Send inquiry"}</span>
            <ArrowUpRight size={18} strokeWidth={1.4} />
          </button>
        </form>
      </div>
    </section>
  );
};

export default ContactSection;
